import { isRatingUnitId, ratingBounds } from '@digital-twins/shared-types'
import { roundHalfAwayFromZero, roundHalfAwayFromZeroAt } from '@digital-twins/deck-ui/foundations/producer-rounding'

/**
 * The top of the scale a figure sits on, from either spelling of the unit:
 * the machine one (`rating_1_5`, whose top is its registered bound) or the
 * human one analysis stamps on a metric ("Mean out of 5"). Without the prose
 * branch a mean prints as a bare "3", which is the number the reader cannot
 * place: three out of what. A machine unit the registry does not know names
 * no scale, so its figure prints bare rather than on an invented one.
 */
function ratingScaleMax(unit: string): number | null {
  const normalized = unit.trim().toLowerCase()
  if (!normalized.startsWith('rating_')) {
    const outOf = normalized.match(/out of (\d+(?:\.\d+)?)/)
    if (outOf) {
      const max = Number(outOf[1])
      return Number.isFinite(max) && max > 0 ? max : null
    }
    return null
  }
  return isRatingUnitId(normalized) ? ratingBounds(normalized)[1] : null
}

/**
 * Rounds half away from zero, the producer's own rule (`roundHalfAwayFromZeroAt`), rather than
 * JS's half-up `Math.round`. `KpiListView` and `SummaryScorecard` pass raw,
 * unrounded means through `formatAbsoluteScore`; without this, a tie like `3.25`
 * printed `3.3` here while Python and deck-ui printed `3.2` for the same figure.
 */
export function formatKpiNumber(value: number): string {
  const rounded = roundHalfAwayFromZeroAt(value, 1)
  return Number.isInteger(value) ? String(value) : String(rounded)
}

/**
 * A share of people as every surface prints it: whole, rounded once half away
 * from zero (owner, 2026-10-16; the producer's `figure_plots.SHARE_DECIMALS`).
 */
export function formatSharePercent(value: number): string {
  return `${roundHalfAwayFromZero(value) + 0}%`
}

/** Fixed decimal for a scale or index figure. Does not change percent formatting. */
export function formatFixedDecimal(value: number, decimals: number): string {
  return roundHalfAwayFromZeroAt(value, decimals).toFixed(decimals)
}

export function isShareUnit(unit: string): boolean {
  const normalized = unit.trim().toLowerCase()
  if (normalized.startsWith('rating_')) {
    return false
  }
  return (
    normalized === 'percent' ||
    normalized === 'percent_0_100' ||
    normalized === 'binary_yes_no' ||
    normalized.includes('yes_share') ||
    normalized.includes('yes-share') ||
    normalized.includes('favorable')
  )
}

/**
 * Is this share unit already expressed 0-100, or is it a 0-1 fraction?
 *
 * Decided by the UNIT, never by the magnitude of the value. The magnitude
 * heuristic it replaces (`Math.abs(value) <= 1 ? value * 100 : value`) printed
 * a legitimate 1% as 100%: `percent_0_100` carries podium shares where one
 * respondent in a hundred is exactly `1.0`, which the heuristic read as the
 * fraction 1.0 and multiplied. A unit that names its own scale does not need
 * to be guessed at.
 */
export function shareIsFraction(unit: string): boolean {
  const normalized = unit.trim().toLowerCase()
  return normalized !== 'percent' && normalized !== 'percent_0_100'
}

export function formatAbsoluteScore(value: number, unit: string, decimals?: number): string {
  const max = ratingScaleMax(unit)
  if (max != null) {
    const places = decimals ?? 1
    return `${formatFixedDecimal(value, places)}/${max}`
  }
  if (isShareUnit(unit)) {
    const pct = shareIsFraction(unit) ? value * 100 : value
    if (decimals != null) {
      return `${formatFixedDecimal(pct, decimals)}%`
    }
    return formatSharePercent(pct)
  }
  if (decimals != null) {
    return formatFixedDecimal(value, decimals)
  }
  return formatKpiNumber(value)
}

export interface LiftFigureSet {
  pre: string
  post: string
  delta: string
  roundingNote: boolean
}

export interface LiftDisplayDecimals {
  decimals: number
  roundingNote: boolean
}

function liftTripletHoldsAt(pre: number, post: number, delta: number, decimals: number): boolean {
  const shown = roundHalfAwayFromZeroAt(post, decimals) - roundHalfAwayFromZeroAt(pre, decimals)
  return Math.abs(shown - roundHalfAwayFromZeroAt(delta, decimals)) < 1 / 10 ** (decimals + 1)
}

/**
 * The decimals ONE LIFT GRID's figures print at, over every triplet it has.
 *
 * The rule, shared with Python's `paired_display_decimals` (the deck's paired
 * grid, its writer and the Summary writers, all at grid scope): 1dp when every
 * triplet's rounded after minus rounded before equals its rounded change at
 * 1dp, else 2dp; the note is raised when 2dp still does not reconcile for some
 * triplet. Pass every triplet of the grid (every measure, asset and grid row
 * reading the same post question against the same baseline), not only the one
 * on screen, so the grid prints at the same decimals on every asset, every
 * measure and every surface (#183: consideration printed 2.7 -> 3.5 here and
 * 2.67 -> 3.50 in the deck; #185: favourability at 2dp beside consideration at
 * 1dp).
 *
 * `baseDecimals` is where the rule starts: 1 for means (the rule above), 0 for
 * shares read in percentage points (plots plan F, kpi spec: "40% -> 44%,
 * +4 pp", rounded to the nearest point, one more place only when whole points
 * do not reconcile).
 */
export function liftDisplayDecimals(
  triplets: ReadonlyArray<readonly [number, number, number]>,
  baseDecimals = 1,
): LiftDisplayDecimals {
  function holdsAt(decimals: number): boolean {
    return triplets.every(([pre, post, delta]) => liftTripletHoldsAt(pre, post, delta, decimals))
  }

  if (holdsAt(baseDecimals)) {
    return { decimals: baseDecimals, roundingNote: false }
  }
  return { decimals: baseDecimals + 1, roundingNote: !holdsAt(baseDecimals + 1) }
}

/** The typographic minus the kpi spec prints a fall with ("−0.2", "−4 pp"). */
const MINUS_SIGN = '\u2212'

/** A signed change at `decimals`: "+4", "−0.2", and "0" with no sign. */
function signedChange(value: number, decimals: number): string {
  const shown = roundHalfAwayFromZeroAt(value, decimals)
  const magnitude = formatFixedDecimal(Math.abs(shown), decimals)
  if (shown > 0) {
    return `+${magnitude}`
  }
  return shown < 0 ? `${MINUS_SIGN}${magnitude}` : magnitude
}

/**
 * Render pre, post and the measured delta so the displayed arithmetic holds.
 *
 * Never recompute delta from rounded endpoints. Decimals come from
 * `liftDisplayDecimals` over `gridTriplets` (every triplet of this lift grid);
 * without them, over this triplet alone.
 *
 * The check rounds by the same rule the figures print with, half away from zero
 * (`roundHalfAwayFromZeroAt`, Python's and deck-ui's rule). A half-up check passed
 * #182's 3.375 -> 4.125 (+0.75) as 3.38 -> 4.13 while the printer showed 4.12.
 */
export function formatLiftTriplet(
  pre: number,
  post: number,
  delta: number,
  unit: string,
  gridTriplets?: ReadonlyArray<readonly [number, number, number]>,
): LiftFigureSet {
  if (unit === NPS_POINTS_UNIT) {
    return formatNpsLiftTriplet(pre, post, delta, gridTriplets)
  }
  const isShare = isShareUnit(unit)
  // A share is read, reconciled and rounded in percentage points: a 0-1
  // fraction is scaled first, never rounded as a fraction (0.04 at one decimal
  // was 0.0, which printed a real four-point change as "+0.0 pp").
  const scale = isShare && shareIsFraction(unit) ? 100 : 1
  const triplets = (
    gridTriplets && gridTriplets.length > 0 ? gridTriplets : [[pre, post, delta] as const]
  ).map(([a, b, c]) => [a * scale, b * scale, c * scale] as const)
  const { decimals, roundingNote } = liftDisplayDecimals(triplets, isShare ? 0 : 1)
  // A change in a share is in percentage points, never "%" (plots plan F,
  // kpi 1): "+4 pp" is four points on the share, "+4%" read as a ratio.
  const deltaLabel = isShare
    ? `${signedChange(delta * scale, decimals)} pp`
    : signedChange(delta, decimals)
  return {
    pre: formatAbsoluteScore(pre, unit, decimals),
    post: formatAbsoluteScore(post, unit, decimals),
    delta: deltaLabel,
    roundingNote,
  }
}

/**
 * The unit of an NPS score movement (decision 20): the score runs -100 to 100
 * and its change is in points. Not a stored unit; the lift card passes it for
 * its NPS view.
 */
export const NPS_POINTS_UNIT = 'nps_points'

/**
 * An NPS score (a level, -100 to 100) at `decimals`, always signed (owner
 * decision 2026-10-01): "+33", "−25", "0". The same rule as the deck's
 * `formatNpsScore`; a change keeps `signedChange` and its unit.
 */
export function formatNpsScore(value: number, decimals = 0): string {
  return signedChange(value, decimals)
}

/**
 * An NPS before/after in whole points ("−25 -> +33, +58 pts"): the levels
 * print like the KPI card's score (always signed), the change carries its
 * unit. One more place only when whole points do not reconcile (the share
 * rule).
 */
function formatNpsLiftTriplet(
  pre: number,
  post: number,
  delta: number,
  gridTriplets?: ReadonlyArray<readonly [number, number, number]>,
): LiftFigureSet {
  const triplets = gridTriplets && gridTriplets.length > 0 ? gridTriplets : [[pre, post, delta] as const]
  const { decimals, roundingNote } = liftDisplayDecimals(triplets, 0)
  return {
    pre: formatNpsScore(pre, decimals),
    post: formatNpsScore(post, decimals),
    delta: `${signedChange(delta, decimals)} pts`,
    roundingNote,
  }
}
